import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Languages, LayoutGrid, LogOut, Moon, RefreshCw, Sun, UserRound, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { GymVizLogo } from "@/components/brand/GymVizLogo";
import { PendingReviewsPrompt } from "@/components/member/PendingReviewsPrompt";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { memberLogout, memberMe } from "@/lib/api/member";
import { useMemberAuthStore } from "@/lib/auth/memberStore";
import { useOfflineSync } from "@/lib/offline/useOfflineSync";
import { ThemeProvider } from "@/lib/theme/ThemeProvider";
import { useThemeContext } from "@/lib/theme/useThemeContext";
import { cn } from "@/lib/utils/cn";
import { ErasureGraceBanner } from "@/pages/member/MemberPrivacyPage";
import { ReConsentBanner } from "@/pages/member/ReConsentBanner";
import { getInitials } from "@/pages/member/ui/styles";
import {
  BOTTOM_CENTER,
  BOTTOM_LEFT,
  BOTTOM_RIGHT,
  NAV_GROUPS,
  type MemberNavItem,
} from "@/pages/member/ui/nav";

const DARK_KEY = "gymviz.member-dark";

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const saved = localStorage.getItem(DARK_KEY);
    if (saved === "1") { document.documentElement.classList.add("dark"); setIsDark(true); }
    else if (saved === "0") { document.documentElement.classList.remove("dark"); setIsDark(false); }
  }, []);
  const toggle = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem(DARK_KEY, next ? "1" : "0");
  };
  return { isDark, toggle };
}

function Avatar({ initials, size = 36 }: { initials: string; size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 select-none items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-700 font-semibold text-white shadow-[0_2px_8px_rgb(var(--color-primary-600)/0.35)] ring-2 ring-surface"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials || <UserRound className="h-4 w-4" aria-hidden="true" />}
    </span>
  );
}

/** Inner shell — must be a child of ThemeProvider so useThemeContext works. */
function MemberPortalShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const cachedMember = useMemberAuthStore((s) => s.member);
  // Same key as Home — one request, shared cache; covers sessions restored without a cached profile.
  const { data: freshMember } = useQuery({ queryKey: ["member", "me"], queryFn: memberMe, initialData: cachedMember ?? undefined, staleTime: 60_000 });
  const member = freshMember ?? cachedMember;
  const { pendingCount, flush } = useOfflineSync();
  const [syncing, setSyncing] = useState(false);
  const { logoUrl, legalName } = useThemeContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark, toggle: toggleDark } = useDarkMode();

  // Close the mobile sheet on any navigation and scroll to top.
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  async function onLogout() {
    await memberLogout();
    navigate("/member/login", { replace: true });
  }
  const toggleLocale = () => void i18n.changeLanguage(i18n.language === "ar" ? "en" : "ar");
  const label = (item: MemberNavItem) => t(item.labelKey, item.fallback);

  const logoLabel = legalName ?? t("app.name");
  const fullName = member?.full_name_en ?? "";
  const initials = fullName ? getInitials(fullName) : "";
  const brand = logoUrl ? (
    <img src={logoUrl} alt={logoLabel} className="h-8 max-w-[140px] object-contain" />
  ) : (
    <GymVizLogo variant="full" size={30} ariaLabel={logoLabel} />
  );

  const syncBadge = pendingCount > 0 && (
    <button
      type="button"
      onClick={async () => {
        setSyncing(true);
        navigate("/member/workout");
        await flush();
        setSyncing(false);
      }}
      disabled={syncing}
      className="inline-flex items-center gap-1.5 rounded-full bg-warning-500/[0.12] px-3 py-1.5 text-xs font-semibold text-warning-500 transition hover:bg-warning-500/20 disabled:opacity-60"
    >
      <RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} aria-hidden="true" />
      {syncing ? t("memberUi.syncing", "Syncing…") : t("memberPortal.pendingSets", { count: pendingCount })}
    </button>
  );

  const accountMenu = (align: "start" | "end", trigger: React.ReactNode) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} sideOffset={8} className="w-64 rounded-2xl p-1.5">
        <DropdownMenuLabel className="flex items-center gap-3 px-2 py-2">
          <Avatar initials={initials} size={36} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-ink-900">{fullName}</span>
            <span className="block truncate text-xs font-normal text-ink-500">{member?.email}</span>
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/member/account")} className="gap-2.5 rounded-xl px-2.5 py-2">
          <UserRound className="h-4 w-4" aria-hidden="true" /> {t("memberUi.nav.account", "Account & privacy")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleDark(); }} className="gap-2.5 rounded-xl px-2.5 py-2">
          {isDark ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
          {isDark ? t("memberUi.lightMode", "Light mode") : t("memberUi.darkMode", "Dark mode")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={toggleLocale} className="gap-2.5 rounded-xl px-2.5 py-2">
          <Languages className="h-4 w-4" aria-hidden="true" />
          {i18n.language === "ar" ? "English" : "العربية"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} className="gap-2.5 rounded-xl px-2.5 py-2 text-danger-500 focus:text-danger-500">
          <LogOut className="h-4 w-4" aria-hidden="true" /> {t("memberPortal.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-dvh bg-bg">
      {/* ═══ Desktop sidebar (lg+) ═══ */}
      <aside className="fixed inset-y-0 start-0 z-30 hidden w-[272px] flex-col border-e border-border-100 bg-surface/80 backdrop-blur-xl lg:flex">
        <div className="flex h-[72px] items-center px-6">{brand}</div>

        <nav aria-label={t("memberUi.navLabel", "Member navigation")} className="flex-1 overflow-y-auto px-3 pb-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.key} className="mt-4 first:mt-1">
              <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-400">
                {t(group.labelKey, group.fallback)}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          "group relative flex h-11 items-center gap-3 rounded-full px-4 text-sm font-medium transition-colors",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                          isActive ? "text-primary-800 dark:text-primary-200" : "text-ink-700 hover:bg-ink-900/[0.04] dark:hover:bg-white/[0.05]",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <motion.span
                              layoutId="member-sidebar-active"
                              className="absolute inset-0 rounded-full bg-primary-500/[0.14]"
                              transition={{ type: "spring", stiffness: 500, damping: 40 }}
                            />
                          )}
                          <item.icon className={cn("relative h-[18px] w-[18px]", isActive ? "text-primary-700 dark:text-primary-300" : "text-ink-500")} aria-hidden="true" />
                          <span className="relative truncate">{label(item)}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-border-100 p-3">
          {accountMenu(
            "start",
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-2xl p-2 text-start transition-colors hover:bg-ink-900/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-white/[0.05]"
            >
              <Avatar initials={initials} size={40} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink-900">{fullName}</span>
                <span className="block truncate text-xs tabular-nums text-ink-500">{member?.member_code}</span>
              </span>
            </button>,
          )}
        </div>
      </aside>

      {/* ═══ Mobile / tablet top app bar (<lg) ═══ */}
      <header className="sticky top-0 z-30 border-b border-border-100 bg-bg/80 backdrop-blur-xl lg:hidden" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="flex h-14 items-center gap-3 px-4">
          <div className="shrink-0">{brand}</div>
          <div className="ms-auto flex items-center gap-2">
            {syncBadge}
            {accountMenu(
              "end",
              <button type="button" aria-label={t("memberUi.accountMenu", "Account menu")} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                <Avatar initials={initials} size={34} />
              </button>,
            )}
          </div>
        </div>
      </header>

      {/* ═══ Content ═══ */}
      <div className="lg:ps-[272px]">
        {pendingCount > 0 && <div className="hidden justify-end px-8 pt-5 lg:flex">{syncBadge}</div>}
        <ErasureGraceBanner />
        <ReConsentBanner />
        <main className="mx-auto w-full max-w-6xl px-4 pb-32 pt-5 sm:px-6 lg:px-10 lg:pb-12 lg:pt-8">
          {/* Keyed fade-in only — an exit animation would render the *new* Outlet twice. */}
          <motion.div
            key={location.pathname}
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>

      <PendingReviewsPrompt />

      {/* ═══ Mobile bottom navigation (<lg) ═══ */}
      <nav
        aria-label={t("memberUi.navLabel", "Member navigation")}
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border-100 bg-surface/90 backdrop-blur-xl lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="mx-auto flex h-16 max-w-lg items-stretch px-2">
          {BOTTOM_LEFT.map((item) => <BottomTab key={item.to} item={item} label={label(item)} />)}
          <li className="flex flex-1 items-start justify-center">
            <NavLink
              to={BOTTOM_CENTER.to}
              aria-label={label(BOTTOM_CENTER)}
              className="-mt-5 flex flex-col items-center gap-1 focus-visible:outline-none"
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "inline-flex h-14 w-14 items-center justify-center rounded-[20px] bg-gradient-to-br from-primary-500 to-primary-700 text-white",
                      "shadow-[0_8px_20px_rgb(var(--color-primary-600)/0.45)] ring-4 ring-bg transition-transform active:scale-95",
                      isActive && "from-primary-600 to-primary-800",
                    )}
                  >
                    <BOTTOM_CENTER.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <span className={cn("text-[11px] font-medium", isActive ? "text-primary-700 dark:text-primary-300" : "text-ink-500")}>
                    {t("memberUi.nav.qrShort", "Check in")}
                  </span>
                </>
              )}
            </NavLink>
          </li>
          {BOTTOM_RIGHT.map((item) => <BottomTab key={item.to} item={item} label={label(item)} />)}
          <li className="flex flex-1">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-expanded={menuOpen}
              className="flex w-full flex-col items-center justify-center gap-1 focus-visible:outline-none"
            >
              <span className={cn("inline-flex h-8 w-16 items-center justify-center rounded-full transition-colors", menuOpen && "bg-primary-500/[0.16]")}>
                <LayoutGrid className={cn("h-[22px] w-[22px]", menuOpen ? "text-primary-700 dark:text-primary-300" : "text-ink-500")} aria-hidden="true" />
              </span>
              <span className={cn("text-[11px] font-medium", menuOpen ? "text-primary-700 dark:text-primary-300" : "text-ink-500")}>
                {t("memberUi.nav.menu", "Menu")}
              </span>
            </button>
          </li>
        </ul>
      </nav>

      {/* ═══ Mobile "Menu" sheet ═══ */}
      <AnimatePresence>
        {menuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label={t("memberUi.nav.menu", "Menu")}>
            <motion.div
              className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-[28px] bg-surface px-4 pb-6 pt-2 shadow-[0_-12px_40px_rgba(0,0,0,0.18)]"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}
              initial={reduce ? { opacity: 0 } : { y: "100%" }}
              animate={reduce ? { opacity: 1 } : { y: 0 }}
              exit={reduce ? { opacity: 0 } : { y: "100%" }}
              transition={{ type: "spring", stiffness: 420, damping: 40 }}
              drag={reduce ? false : "y"}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.4 }}
              onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) setMenuOpen(false); }}
            >
              <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-ink-900/15 dark:bg-white/20" aria-hidden="true" />

              <div className="mb-2 flex items-center gap-3 rounded-2xl bg-bg p-3">
                <Avatar initials={initials} size={44} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink-900">{fullName}</p>
                  <p className="truncate text-xs tabular-nums text-ink-500">{member?.member_code}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  aria-label={t("memberUi.close", "Close")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-900/[0.05]"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              {NAV_GROUPS.map((group) => (
                <div key={group.key} className="mt-4">
                  <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-400">
                    {t(group.labelKey, group.fallback)}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            "flex flex-col items-center gap-2 rounded-2xl px-2 py-3.5 text-center transition-colors",
                            isActive ? "bg-primary-500/[0.14] text-primary-800 dark:text-primary-200" : "bg-bg text-ink-700 hover:bg-ink-900/[0.04]",
                          )
                        }
                      >
                        <item.icon className="h-[22px] w-[22px]" aria-hidden="true" />
                        <span className="line-clamp-2 text-xs font-medium leading-tight">{label(item)}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}

              <div className="mt-5 grid grid-cols-2 gap-2">
                <button type="button" onClick={toggleDark} className="flex items-center justify-center gap-2 rounded-2xl bg-bg py-3 text-sm font-medium text-ink-700">
                  {isDark ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
                  {isDark ? t("memberUi.lightMode", "Light mode") : t("memberUi.darkMode", "Dark mode")}
                </button>
                <button type="button" onClick={toggleLocale} className="flex items-center justify-center gap-2 rounded-2xl bg-bg py-3 text-sm font-medium text-ink-700">
                  <Languages className="h-4 w-4" aria-hidden="true" />
                  {i18n.language === "ar" ? "English" : "العربية"}
                </button>
              </div>
              <button
                type="button"
                onClick={onLogout}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-danger-500/10 py-3 text-sm font-semibold text-danger-500"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" /> {t("memberPortal.logout")}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BottomTab({ item, label }: { item: MemberNavItem; label: string }) {
  return (
    <li className="flex flex-1">
      <NavLink to={item.to} end={item.end} className="flex w-full flex-col items-center justify-center gap-1 focus-visible:outline-none">
        {({ isActive }) => (
          <>
            <span className="relative inline-flex h-8 w-16 items-center justify-center">
              {isActive && (
                <motion.span
                  layoutId="member-bottom-active"
                  className="absolute inset-0 rounded-full bg-primary-500/[0.16]"
                  transition={{ type: "spring", stiffness: 500, damping: 38 }}
                />
              )}
              <item.icon className={cn("relative h-[22px] w-[22px]", isActive ? "text-primary-700 dark:text-primary-300" : "text-ink-500")} aria-hidden="true" />
            </span>
            <span className={cn("text-[11px]", isActive ? "font-semibold text-primary-700 dark:text-primary-300" : "font-medium text-ink-500")}>{label}</span>
          </>
        )}
      </NavLink>
    </li>
  );
}

/** Member portal shell: sidebar on desktop, app bar + bottom nav on mobile. */
export default function MemberPortalLayout() {
  return (
    <ThemeProvider>
      <MemberPortalShell />
    </ThemeProvider>
  );
}
